import React, { useState, useRef, useCallback } from 'react'
import {
  Box, Chip, CircularProgress, Collapse,
  Divider, IconButton, Tooltip, Typography,
} from '@mui/material'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { useApp, useAppActions, useAppProject } from '../AppContext'
import { addToast } from '../services/Toasts'
import { FS_MICRO, FS_XS, FS_LG, FS_XL, ICON_XS, ICON_SM, ICON_MD, ICON_LG } from '../common/theme'
import taapi from '../services/taapi'
import { pmSaveQuestion } from '../services/ProjectManagerService'
import { runScore, runImprove } from '../services/QuestionActions'
import TaEvalScored from '../comps/TaEvalScored'
import { FancyButton } from '../comps/FancyButtons'
import TaEvalImprovements from '../comps/TaEvalImprovements'
import TaRagExpandable from '../comps/TaRagExpandable'
import TaQueryBar from '../comps/TaQueryBar'
import TaQuestionPicker from '../comps/TaQuestionPicker'
import TaSourceMatch from '../comps/TaSourceMatch'
import TaWordCount from '../comps/TaWordCount'
import TaResponse from '../comps/TaResponse'
import TabHeader from '../comps/TabHeader'
import { Question } from '../types'
import { hasAnswerText } from '../common/common'

// ─── Main component ───────────────────────────────────────────────────────────

export default function ScoreFixTab() {
  console.log('ScoreFixTab render')
  const { isProcessing } = useApp()
  const { currentProject, currentQuestion } = useAppProject()
  const { setCurrentQuestion, setIsProcessing, updateQuestion } = useAppActions()

  const [viewSelected, setViewSelected] = useState<'origanswer' | 'fixedanswer'>('origanswer')
  const [beView, setBeView] = useState({
    isprocessing: false,
    showquestion: true,
    showremtext: true,
    fixedresult: null as any,
  })
  const [leftPct, setLeftPct] = useState(42)
  const [topPx, setTopPx] = useState(260)
  const containerRef = useRef<HTMLDivElement>(null)

  const projectQuestions = currentProject?.questions ?? []
  const questionIndex = projectQuestions.findIndex(
    (q: any) => q.questionsha === (currentQuestion as any)?.questionsha
  )

  const groups = (currentQuestion as any)?.bideval?.checkpointeval?.groups ?? []
  const allImprovements = groups.flatMap((g: any) =>
    (g.improvementcategories ?? []).flatMap((c: any) => c.improvements ?? [])
  )
  const fixesSelectedCount = allImprovements.filter((imp: any) => imp.fix).length

  const hasAnswer = hasAnswerText(currentQuestion);
  const isScored = typeof (currentQuestion as any)?.bideval?.scoreint === 'number'
    && (currentQuestion as any).bideval.scoreint >= 0

  const handleEval = async () => {
    if (!currentQuestion || isProcessing) return
    if (!currentProject) {
      addToast('error', 'No project loaded.')
      return
    }
    // Match the rest of the app (BidQuestion / WordWizard) — score
    // via the centralised `runScore` foreground action so the result
    // lands on the question through `updateQuestion` and every
    // surface that watches the question sees the new bideval.
    setIsProcessing(true)
    try {
      const r = await runScore(currentQuestion as Question, currentProject, updateQuestion)
      if (!r.success) addToast('error', r.msg ?? 'Failed to estimate score.')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleImprove = async () => {
    if (!currentQuestion || isProcessing) return
    if (!currentProject) {
      addToast('error', 'No project loaded.')
      return
    }
    // Same foreground-action pattern as `handleEval` — runImprove
    // posts to /api2/pmforegroundprocess with `genimprovements=true`,
    // and dispatchCompletedTask writes the returned bideval (with
    // improvementcategories baked in) back through updateQuestion.
    setIsProcessing(true)
    try {
      const r = await runImprove(currentQuestion as Question, currentProject, updateQuestion)
      if (!r.success) addToast('error', r.msg ?? 'Failed to suggest improvements.')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleApplyFix = (improvement: any) => {
    improvement.fix = true
    setCurrentQuestion({ ...(currentQuestion as any) })
  }

  const handleDeleteFix = (improvement: any) => {
    improvement.fix = false
    setCurrentQuestion({ ...(currentQuestion as any) })
  }

  const handleFixAll = useCallback(async () => {
    if (!currentQuestion || beView.isprocessing) return
    setBeView(v => ({ ...v, isprocessing: true }))
    try {
      const result = await taapi.post('/api2/befix', {
        questionsha: (currentQuestion as any).questionsha,
        bideval: (currentQuestion as any).bideval,
      })
      if (result?.fixedresult) {
        setBeView(v => ({ ...v, fixedresult: result.fixedresult, isprocessing: false }))
        setViewSelected('fixedanswer')
      }
    } catch {
      addToast('error', 'Failed to apply fixes.')
      setBeView(v => ({ ...v, isprocessing: false }))
    }
  }, [currentQuestion, beView.isprocessing, addToast])

  const handleClearFixes = () => {
    allImprovements.forEach((imp: any) => { imp.fix = false })
    setCurrentQuestion({ ...(currentQuestion as any) })
  }

  const handleSave = async () => {
    if (!currentQuestion) return
    try {
      await pmSaveQuestion({
        questionsha: (currentQuestion as any).questionsha,
        projectsha: (currentQuestion as any).projectsha,
        answerobj: (currentQuestion as any).answerobj,
        bideval: (currentQuestion as any).bideval,
      })
      addToast('success', 'Saved.')
    } catch {
      addToast('error', 'Failed to save.')
    }
  }

  const handleApplyFixed = async () => {
    if (!currentQuestion || !beView.fixedresult) return
    try {
      const newAnswer = beView.fixedresult.fixedhtml
      await pmSaveQuestion({
        questionsha: (currentQuestion as any).questionsha,
        projectsha: (currentQuestion as any).projectsha,
        answerobj: { ...(currentQuestion as any).answerobj, answer: newAnswer },
      })
      setCurrentQuestion({
        ...(currentQuestion as any),
        answerobj: { ...(currentQuestion as any).answerobj, answer: newAnswer },
      })
      setBeView(v => ({ ...v, fixedresult: null }))
      setViewSelected('origanswer')
      addToast('success', 'Fixed answer applied.')
    } catch {
      addToast('error', 'Failed to apply fixed answer.')
    }
  }

  const handleHorizResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const container = containerRef.current
    if (!container) return
    const onMove = (ev: MouseEvent) => {
      const rect = container.getBoundingClientRect()
      const pct = ((ev.clientX - rect.left) / rect.width) * 100
      setLeftPct(Math.max(20, Math.min(70, pct)))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [])

  const handleVertResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const startY = e.clientY
    const startH = topPx
    const onMove = (ev: MouseEvent) => {
      setTopPx(Math.max(80, Math.min(480, startH + ev.clientY - startY)))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [topPx])

  const VIEW_TABS = [
    { key: 'origanswer' as const, label: 'Original Answer' },
    { key: 'fixedanswer' as const, label: 'Fixed Answer' },
  ]

  return (
    <Box data-testid="sf_root" ref={containerRef} sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* ── Left panel ──────────────────────────────────────────────── */}
      <Box data-testid="sf_left_panel" sx={{ width: `${leftPct}%`, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', overflow: 'hidden' }}>

        {/* ── Toolbar ──────────────────────────────────────────────────── */}
        <Box sx={{ flexShrink: 0, px: 0.75, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ opacity: 0.55, fontSize: '0.7rem' }}>Evaluation</Typography>
          <Box sx={{ flex: 1 }} />
          {isProcessing && <CircularProgress size={12} />}
          <Tooltip title="Estimate Score">
            <span>
              <IconButton data-testid="sf_eval_btn" size="small" disabled={isProcessing || !hasAnswer} onClick={handleEval} sx={{ p: 0.375 }}>
                <AssessmentOutlinedIcon sx={{ fontSize: ICON_SM }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Suggest Improvements">
            <span>
              <IconButton data-testid="sf_improve_btn" size="small" disabled={isProcessing || !isScored} onClick={handleImprove} sx={{ p: 0.375 }}>
                <LightbulbOutlinedIcon sx={{ fontSize: ICON_SM }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Save Question">
            <IconButton data-testid="sf_save_btn" size="small" onClick={handleSave} sx={{ p: 0.375 }}>
              <SaveOutlinedIcon sx={{ fontSize: ICON_SM }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />

        {/* ── View tabs ────────────────────────────────────────────────── */}
        <TabHeader
          tabs={VIEW_TABS}
          active={viewSelected}
          onChange={setViewSelected}
          testIdPrefix="scorefix_view"
        />

        {/* ── Scored section ────────────────────────────────────────────── */}
        <Box sx={{ height: topPx, flexShrink: 0, overflowY: 'auto' }}>
          <TaEvalScored
            bideval={(currentQuestion as any)?.bideval}
            question={currentQuestion}
            onEval={handleEval}
          />
        </Box>

        {/* ── Vertical resize ───────────────────────────────────────────── */}
        <Box
          onMouseDown={handleVertResizeStart}
          sx={{
            height: 5, flexShrink: 0, cursor: 'row-resize',
            borderTop: 1, borderBottom: 1, borderColor: 'divider',
            '&:hover': { bgcolor: 'action.hover' },
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <MoreHorizIcon sx={{ fontSize: ICON_MD, opacity: 0.35 }} />
        </Box>

        {/* ── Improvements section ──────────────────────────────────────── */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <TaEvalImprovements
            improvements={allImprovements}
            question={currentQuestion}
            onImprove={handleImprove}
            onFixAll={handleFixAll}
            onApplyFix={handleApplyFix}
            onDeleteFix={handleDeleteFix}
            onClearFixes={handleClearFixes}
          />
        </Box>

        {/* ── Apply fixes footer ───────────────────────────────────────── */}
        {fixesSelectedCount > 0 && (
          <>
            <Divider />
            <Box sx={{ flexShrink: 0, px: 0.75, py: 0.5 }}>
              <FancyButton
                testId="sf_apply_fixes_btn"
                solid
                disabled={beView.isprocessing}
                onClick={handleFixAll}
              >
                {beView.isprocessing && <CircularProgress size={12} color="inherit" sx={{ mr: 1 }} />}
                {fixesSelectedCount === 1 ? 'Apply Fix' : `Apply ${fixesSelectedCount} Fixes`}
              </FancyButton>
            </Box>
          </>
        )}
      </Box>

      {/* ── Horizontal resize ───────────────────────────────────────── */}
      <Box
        onMouseDown={handleHorizResizeStart}
        sx={{
          width: 7, flexShrink: 0, cursor: 'col-resize',
          borderLeft: 1, borderRight: 1, borderColor: 'divider',
          '&:hover': { bgcolor: 'action.hover' },
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <MoreVertIcon sx={{ fontSize: ICON_LG, opacity: 0.35 }} />
      </Box>

      {/* ── Right panel ─────────────────────────────────────────────── */}
      <Box data-testid="sf_right_panel" sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* ── Question picker ───────────────────────────────────────────── */}
        {currentQuestion && (
          <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider', px: 1, py: 0.5 }}>
            <TaQuestionPicker
              questions={projectQuestions}
              currentIndex={questionIndex}
              onChange={i => {
                if (i >= 0 && i < projectQuestions.length) setCurrentQuestion(projectQuestions[i])
                else setCurrentQuestion(null)
              }}
              title="Question"
            />
          </Box>
        )}

        <Box sx={{ flex: 1, overflowY: 'auto' }}>

          {/* ── Question section ──────────────────────────────────────────── */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 0.75, py: 0.4, gap: 0.5 }}>
              <TaRagExpandable question={currentQuestion} />
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_XS }}>Question</Typography>
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Collapse">
                <IconButton
                  size="small"
                  onClick={() => setBeView(v => ({ ...v, showquestion: !v.showquestion }))}
                  sx={{ p: 0.25 }}
                >
                  {beView.showquestion ? <ExpandLessIcon sx={{ fontSize: ICON_XS }} /> : <ExpandMoreIcon sx={{ fontSize: ICON_XS }} />}
                </IconButton>
              </Tooltip>
            </Box>
            <Collapse in={beView.showquestion}>
              <Box sx={{ px: 1, pb: 0.75 }}>
                <TaQueryBar
                  htmlContent={(currentQuestion as any)?.question ?? ''}
                  onChange={() => { }}
                  editable={false}
                  disabled={!currentQuestion}
                />
              </Box>
            </Collapse>
          </Box>

          {/* ── Answer view tabs ──────────────────────────────────────────── */}
          <TabHeader
            tabs={VIEW_TABS}
            active={viewSelected}
            onChange={setViewSelected}
            testIdPrefix="scorefix_answer_view"
          />

          {/* ── Original answer view ──────────────────────────────────────── */}
          {viewSelected === 'origanswer' && (
            <Box sx={{ p: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                <TaSourceMatch answerObj={(currentQuestion as any)?.answerobj} />
                <Box sx={{ flex: 1 }} />
                <TaWordCount
                  structure={(currentQuestion as Question)?.answerobj?.answerstructure}
                  replylimit={(currentQuestion as any)?.replylimit}
                />
              </Box>
              {/* Render the answer from the canonical answerstructure
                  via TaResponse. Read-only here — Score & Fix surfaces
                  the answer as context for the eval breakdown on the
                  right; edits should go through BidQuestion or the
                  Word Wizard. */}
              <TaResponse
                structure={(currentQuestion as Question)?.answerobj?.answerstructure}
                onChange={() => { /* read-only */ }}
                editable={false}
                id="scorefix_orig_answer"
              />
            </Box>
          )}

          {/* ── Fixed answer view ─────────────────────────────────────────── */}
          {viewSelected === 'fixedanswer' && (
            <Box sx={{ p: 1 }}>
              {/* Before → After metrics */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                <TaSourceMatch answerObj={(currentQuestion as any)?.answerobj} />
                <ArrowRightAltIcon sx={{ fontSize: ICON_MD, opacity: 0.5 }} />
                <TaSourceMatch answerObj={beView.fixedresult?.newanswerobj} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                <TaWordCount
                  structure={currentQuestion?.answerobj?.answerstructure}
                  replylimit={(currentQuestion as any)?.replylimit} />
                <ArrowRightAltIcon sx={{ fontSize: FS_XL, opacity: 0.5 }} />
                <TaWordCount text={beView.fixedresult?.fixedhtml} replylimit={(currentQuestion as any)?.replylimit} />
                <Box sx={{ flex: 1 }} />
                <Chip
                  size="small"
                  label={beView.showremtext ? 'Show removed' : 'Hide removed'}
                  onClick={() => setBeView(v => ({ ...v, showremtext: !v.showremtext }))}
                  sx={{ height: 18, fontSize: FS_MICRO, cursor: 'pointer' }}
                />
              </Box>
              {beView.fixedresult ? (
                <Typography
                  component="div"
                  sx={{
                    fontSize: FS_LG, lineHeight: 1.6,
                    '& del': { display: beView.showremtext ? undefined : 'none', opacity: 0.45 },
                  }}
                  dangerouslySetInnerHTML={{ __html: beView.fixedresult.fixedhtml ?? '' }}
                />
              ) : (
                <Typography variant="caption" sx={{ opacity: 0.4, fontSize: FS_XS }}>
                  No fixed answer yet. Select improvements and apply fixes.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* ── Apply / Cancel footer ────────────────────────────────────── */}
        {beView.fixedresult && (
          <>
            <Divider />
            <Box sx={{ flexShrink: 0, px: 0.75, py: 0.5, display: 'flex', gap: 0.75 }}>
              <FancyButton
                testId="sf_apply_btn"
                solid
                disabled={isProcessing}
                onClick={handleApplyFixed}
              >
                Apply
              </FancyButton>
              <FancyButton
                testId="sf_cancel_btn"
                onClick={() => setBeView(v => ({ ...v, fixedresult: null }))}
              >
                Cancel
              </FancyButton>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
